import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Divider,
  Box,
} from '@mui/material';
import { Print as PrintIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  SelectedProduct,
  Location,
  OrderSummary,
  PaymentConfig,
  OrderChargeDraft,
  ChargeCategory,
} from '../../../../types/sales.types';
import { usePrintReceiptModal } from './PrintReceiptModal.hooks';
import { STORE_INFO } from '../../../../utils/constants';
import { formatCurrency } from '../../../../utils/formatters/money';
import { getPaymentMethodsDisplayLabel } from '../../../../utils/sales/paymentMethodDisplay';
import { SaleNumberDisplay } from '../../shared/SaleNumberDisplay';
import {
  ReceiptContentBox,
  HeaderBox,
  SectionBox,
  ProductBox,
  FlexBetweenBox,
  CenteredBox,
  SignatureBox,
  FooterBox,
} from './PrintReceiptModal.styles';

interface PaymentMethodOption {
  id: string;
  name: string;
  icon: any;
  color: string;
}

export interface PrintReceiptModalProps {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
  branchReceiptNumber?: string | null;
  selectedProducts: SelectedProduct[];
  selectedLocation: Location | null;
  orderSummary: OrderSummary;
  orderCharges?: OrderChargeDraft[];
  chargeCategories?: ChargeCategory[];
  paymentMethodOptions: PaymentMethodOption[];
  paymentConfig: PaymentConfig;
  /** ISO 8601 (createdAt del servidor) para alinear comprobante con la orden */
  orderCompletedAt?: string | null;
}

const PrintReceiptModal: React.FC<PrintReceiptModalProps> = ({
  open,
  onClose,
  orderNumber,
  branchReceiptNumber,
  selectedProducts,
  selectedLocation,
  orderSummary,
  orderCharges = [],
  chargeCategories = [],
  paymentMethodOptions,
  paymentConfig,
  orderCompletedAt,
}) => {
  const { formatDiscountDisplay, handlePrint, receiptDateTime } = usePrintReceiptModal({
    orderNumber,
    orderCompletedAt,
  });

  const paymentMethodDisplayName = useMemo(
    () => getPaymentMethodsDisplayLabel(paymentConfig, paymentMethodOptions),
    [paymentConfig, paymentMethodOptions],
  );

  const categoryLabelByCode = useMemo(
    () => new Map(chargeCategories.map((category) => [category.code, category.label])),
    [chargeCategories],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Comprobante de Venta</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <ReceiptContentBox id="receipt-content">
          {/* Header */}
          <HeaderBox>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {STORE_INFO.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {STORE_INFO.address}
              <br />
              {STORE_INFO.city}
              <br />
              Tel: {STORE_INFO.phone}
              <br />
              NIT: {STORE_INFO.nit}
            </Typography>
          </HeaderBox>

          <Divider sx={{ my: 2 }} />

          {/* Title */}
          <CenteredBox sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              COMPROBANTE DE VENTA
            </Typography>
          </CenteredBox>

          {/* Order Info */}
          <SectionBox>
            <SaleNumberDisplay
              orderCode={orderNumber}
              branchReceiptCode={branchReceiptNumber}
              showLabels
              layout="stack"
            />
            <Typography variant="body2">Fecha y hora: {receiptDateTime}</Typography>
            <Typography variant="body2">Sucursal: {selectedLocation?.name}</Typography>
            <Typography variant="body2">Vendedor: Usuario Sistema</Typography>
          </SectionBox>

          <Divider sx={{ my: 2 }} />

          {/* Products */}
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
            DETALLE DE PRODUCTOS
          </Typography>

          {selectedProducts.map((product) => (
            <ProductBox key={product.warehouseInventoryProduct.id}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {product.warehouseInventoryProduct.product.name}
              </Typography>
              <Typography variant="body2">
                Código:{' '}
                {typeof product.warehouseInventoryProduct.product.sku === 'string'
                  ? product.warehouseInventoryProduct.product.sku
                  : product.warehouseInventoryProduct.product.sku?.code || 'N/A'}
              </Typography>
              <FlexBetweenBox>
                <Typography variant="body2">1 x {formatCurrency(product.originalPrice)}</Typography>
                <Typography variant="body2">{formatCurrency(product.originalPrice)}</Typography>
              </FlexBetweenBox>
              {product.discountValue > 0 && (
                <FlexBetweenBox>
                  <Typography variant="body2">
                    Descuento: -
                    {formatDiscountDisplay(
                      product.discountType,
                      product.discountValue,
                      product.originalPrice,
                    )}
                  </Typography>
                  <Typography variant="body2">
                    -{formatCurrency(product.originalPrice - product.finalPrice)}
                  </Typography>
                </FlexBetweenBox>
              )}
              <FlexBetweenBox sx={{ fontWeight: 600 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">{formatCurrency(product.finalPrice)}</Typography>
              </FlexBetweenBox>
            </ProductBox>
          ))}

          {orderCharges.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                CARGOS ADICIONALES
              </Typography>
              {orderCharges.map((charge) => (
                <Box key={charge.id} sx={{ mb: 1.5 }}>
                  <FlexBetweenBox>
                    <Typography variant="body2">
                      {categoryLabelByCode.get(charge.categoryCode) ?? charge.categoryCode}
                    </Typography>
                    <Typography variant="body2">{formatCurrency(charge.amount)}</Typography>
                  </FlexBetweenBox>
                  {charge.description?.trim() && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {charge.description.trim()}
                    </Typography>
                  )}
                </Box>
              ))}
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Summary */}
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
            RESUMEN
          </Typography>

          <Box sx={{ space: 1 }}>
            <FlexBetweenBox>
              <Typography variant="body2">Subtotal:</Typography>
              <Typography variant="body2">{formatCurrency(orderSummary.subtotal)}</Typography>
            </FlexBetweenBox>
            {orderSummary.totalDiscount > 0 && (
              <FlexBetweenBox>
                <Typography variant="body2">Descuentos:</Typography>
                <Typography variant="body2">
                  -{formatCurrency(orderSummary.totalDiscount)}
                </Typography>
              </FlexBetweenBox>
            )}
            {orderSummary.totalCharges > 0 && (
              <FlexBetweenBox>
                <Typography variant="body2">Cargos adicionales:</Typography>
                <Typography variant="body2">{formatCurrency(orderSummary.totalCharges)}</Typography>
              </FlexBetweenBox>
            )}
            <FlexBetweenBox sx={{ fontWeight: 600 }}>
              <Typography variant="body2">TOTAL:</Typography>
              <Typography variant="body2">{formatCurrency(orderSummary.grandTotal)}</Typography>
            </FlexBetweenBox>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Payment Info */}
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
            INFORMACIÓN DE PAGO
          </Typography>

          <Box sx={{ space: 1 }}>
            <FlexBetweenBox>
              <Typography variant="body2">Método de pago:</Typography>
              <Typography variant="body2">{paymentMethodDisplayName}</Typography>
            </FlexBetweenBox>

            {paymentConfig.payments.length > 0 && (
              <Box sx={{ mt: 1.5, mb: 1 }}>
                {paymentConfig.payments.map((payment) => {
                  const methodName =
                    paymentMethodOptions.find((m) => m.id === payment.method)?.name ??
                    payment.method;
                  const noteText = payment.paymentNotes?.trim() ?? '';

                  return (
                    <Box key={payment.id} sx={{ mb: 1 }}>
                      <FlexBetweenBox>
                        <Typography variant="body2">{methodName}:</Typography>
                        <Typography variant="body2">{formatCurrency(payment.amount)}</Typography>
                      </FlexBetweenBox>
                      {noteText !== '' && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontStyle: 'italic' }}
                        >
                          Nota: {noteText}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {paymentConfig.cashAmount > 0 && (
              <>
                <FlexBetweenBox>
                  <Typography variant="body2">Monto recibido:</Typography>
                  <Typography variant="body2">
                    {formatCurrency(paymentConfig.cashAmount)}
                  </Typography>
                </FlexBetweenBox>
                {orderSummary.change > 0 && (
                  <FlexBetweenBox sx={{ fontWeight: 600 }}>
                    <Typography variant="body2">Cambio:</Typography>
                    <Typography variant="body2">{formatCurrency(orderSummary.change)}</Typography>
                  </FlexBetweenBox>
                )}
              </>
            )}

            {orderSummary.pendingBalance > 0 && (
              <FlexBetweenBox sx={{ fontWeight: 600 }}>
                <Typography variant="body2">Saldo pendiente:</Typography>
                <Typography variant="body2">
                  {formatCurrency(orderSummary.pendingBalance)}
                </Typography>
              </FlexBetweenBox>
            )}

            <FlexBetweenBox sx={{ fontWeight: 600 }}>
              <Typography variant="body2">Estado:</Typography>
              <Typography variant="body2">
                {orderSummary.paymentStatus === 'complete' ? 'PAGADO' : 'PAGO PARCIAL'}
              </Typography>
            </FlexBetweenBox>
          </Box>

          {/* Partial Payment Warning */}
          {orderSummary.paymentStatus === 'partial' && (
            <>
              <Divider sx={{ my: 2 }} />
              <CenteredBox>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  IMPORTANTE:
                </Typography>
                <Typography variant="body2">
                  Venta con saldo pendiente de
                  <br />
                  <strong>{formatCurrency(orderSummary.pendingBalance)}</strong>
                  <br />
                  El cliente deberá completar
                  <br />
                  el pago posteriormente.
                </Typography>
              </CenteredBox>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Footer */}
          <CenteredBox>
            <Typography variant="body2" sx={{ mb: 1 }}>
              ¡Gracias por su compra!
            </Typography>
            <Typography variant="body2">{STORE_INFO.website}</Typography>
            <Typography variant="body2">{STORE_INFO.socialMedia}</Typography>
            <SignatureBox>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption">Firma del cliente</Typography>
            </SignatureBox>
          </CenteredBox>

          <FooterBox>
            <Typography variant="caption" color="text.secondary">
              Venta registrada el {receiptDateTime}
            </Typography>
          </FooterBox>
        </ReceiptContentBox>
      </DialogContent>

      <DialogActions>
        <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />}>
          Imprimir
        </Button>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintReceiptModal;
